import { z } from "zod";
import { defineQuery } from "supabase/database/server.ts";
import { BaseContent } from "./types.ts";
import { utils } from "./utils.ts";

export const getContentList = defineQuery(
  "getContentList",
  {
  input: z.object({
    table: z.string(),
    bypassCache: z.boolean().optional()
  }),
  handler: async ({ db }, { table }) => {
    const statusFilter = utils.checkStatusEnv();
    const regionFilter = await utils.regionFilter();
    const tableName = table.replaceAll("-", "_");

    const isColumnMissingError = (err) =>
      err && (err.code === "42703" || err.code === "PGRST204" ||
        (err.message?.includes("column") && err.message?.includes("does not exist")));

    const withRegion = (q) => regionFilter !== null ? q.in("region", regionFilter) : q;

    let { data, error } = await withRegion(
      db.from(tableName).select("*").in("status", statusFilter)
    ).order("date", { ascending: false, nullsFirst: false }).order("id", { ascending: false });

    if (isColumnMissingError(error)) {
      ({ data, error } = await withRegion(
        db.from(tableName).select("*").in("status", statusFilter)
      ).order("id", { ascending: false }));

      if (isColumnMissingError(error)) {
        ({ data, error } = await db
          .from(tableName)
          .select("*")
          .in("status", statusFilter)
          .order("id", { ascending: false }));
      }
    }

    if (["service", "daily-breads"].includes(table)){
      if (regionFilter !== null) {
        const datesWithRegionContent = new Set(
          data?.filter(item => item.region && item.region !== "all").map(item => item.date)
        );
        if (datesWithRegionContent.size > 0) {
          data = data.filter(item => !(item.region === "all" && datesWithRegionContent.has(item.date)));
        }
      }

      const liveTable = table === "service" ? "service" : "daily-breads";
      const liveVid = await utils.fetchLiveVideo(liveTable);
      if (liveVid){
        const hasEntryAlready = data.some(entry => entry.date === liveVid.date);
        const dateTodayOrBefore = new Date(liveVid.date).getDate() <= new Date().getDate();
        if (!hasEntryAlready && dateTodayOrBefore) data.unshift(liveVid);
      }
      const isProdUrl = typeof window !== 'undefined' && ["lightrainair.co.za", "www.lightrainair.co.za"].includes(window.location.hostname);
      if (liveTable === "service" && !isProdUrl){
        const dryrunVid = await utils.fetchLiveVideo("dryrun");
        if (dryrunVid){
          data.unshift(dryrunVid);
        }
      }
    }

    if (error) throw error;
    return data as BaseContent[];
  },
});

export const getContent = defineQuery(
  "getContent",
  {
  input: z.object({
    table: z.string(),
    id: z.string().optional(),
    date: z.string().optional(),
    url: z.string().optional(),
    bypassCache: z.boolean().optional()
  }),
  handler: async ({ db }, { table, id, date, url }) => {
    const statusFilter = utils.checkStatusEnv();
    const regionFilter = await utils.regionFilter();
    const withRegion = (q) => regionFilter !== null ? q.in("region", regionFilter) : q;
    var liveVid = null;
    var liveDryrun = null;


    if (id) {
      if (id.length === 9 && table === "service"){
        liveDryrun = await utils.fetchLiveVideo("dryrun");

        // Check if liveDryrun matches the requested date first
        if (liveDryrun && liveDryrun.date === `${utils.formatDate(id.replace('-dr', ''))}-dr`) {
          return liveDryrun as BaseContent;
        }
      }

      const { data, error } = await withRegion(
        db.from(table.replaceAll("-", "_")).select("*").in("status", statusFilter)
      ).eq("id", id).single();
      if (error) throw error;
      return data as BaseContent;
    } else if (url) {
      const { data, error } = await withRegion(
        db.from(table.replaceAll("-", "_")).select("*").in("status", statusFilter)
      ).eq("url", url).maybeSingle();
      if (error) throw error;
      if (!data) throw new Error("Content not found");
      return data as BaseContent;
    } else if (date) {
      if (table === "daily-breads"){
        liveVid = await utils.fetchLiveVideo("daily-breads");
      }

      if (table === "service"){
        liveVid = await utils.fetchLiveVideo("service");
      }
      // Convert YYMMDD format to YYYY-MM-DD if needed
      const formattedDate = date.length === 6 ? utils.formatDate(date) : date;

      // Check if liveVid matches the requested date first
      if (liveVid && liveVid.date === formattedDate) {
        return liveVid as BaseContent;
      }

      const { data, error } = await withRegion(
        db.from(table.replaceAll("-", "_")).select("*").in("status", statusFilter)
      ).eq("date", formattedDate).maybeSingle();

      if (error) throw error;
      if (!data && liveVid) return liveVid as BaseContent;
      if (!data) throw new Error("Content not found");
      return data as BaseContent;
    } else {
      const tableName = table.replaceAll("-", "_");
      const isColumnMissingError = (err) =>
        err && (err.code === "42703" || err.code === "PGRST204" ||
          (err.message?.includes("column") && err.message?.includes("does not exist")));

      let { data, error } = await withRegion(
        db.from(tableName).select("*").in("status", statusFilter)
      ).order("date", { ascending: false, nullsFirst: false }).order("id", { ascending: false }).single();

      if (isColumnMissingError(error)) {
        ({ data, error } = await withRegion(
          db.from(tableName).select("*").in("status", statusFilter)
        ).order("id", { ascending: false }));

        if (isColumnMissingError(error)) {
          ({ data, error } = await db
            .from(tableName)
            .select("*")
            .in("status", statusFilter)
            .order("id", { ascending: false }));
        }
      }

      if (error) throw error;
      return data as BaseContent[];
    }
  },
});

export const getCoreLeaders = defineQuery(
  "getCoreLeaders",
  {
  input: z.object({}),
  handler: async ({ db }) => {
    const { data, error } = await db
      .from("core_leaders")
      .select("scj_id")
    if (error) throw error;
    return data as { scj_id: string }[];
  }
});